import "react-calendar/dist/Calendar.css";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Calendar from "react-calendar";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticTimePicker } from "@mui/x-date-pickers/StaticTimePicker";
import { Controller, useForm } from "react-hook-form";
import {
  FormControl,
  FormHelperText,
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  Button,
  useMediaQuery,
} from "@mui/material";
import TextInputField from "./textInputField";
import { retrieveData, storeData } from "../helper/helper";
import { useEffect, useRef, useState } from "react";
import TimeChips from "./timeRender";
import { getBookedJobs } from "../service/api";
import Note from "./note";
import moment from "moment";
export default function DateTimeForm({
  onStepDecrement,
  onStepIncrement,
  getStepTwoData,
}) {
  const defaultValues = {
    date: new Date(),
    time: "",
    otherInformation: "",
    hearAboutUs: "",
  };
  const Schema = yup.object().shape({
    date: yup.string().required("Required Field"),
    time: yup.string().required("Required Field"),
    hearAboutUs: yup.string().required("Required Field"),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
    resolver: yupResolver(Schema),
  });

  const [dateData, setDateData] = useState({
    date: getValues().date,
    dateTimeArray: [],
  });
  const [dateLoading, setDateLoading] = useState(false);
  const platform = [
    "Google",
    "Facebook",
    "Gumtree",
    "Instagram",
    "TikTok",
    "Word of mouth",
    "Other",
  ];
  const onSubmit = (data) => {
    if (data) {
      getStepTwoData(data);
      storeData("stepTwoData", { ...data });
      onStepIncrement();
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const getBookedJobsData = async (date) => {
    if (dateLoading) return;
    setDateLoading(true);
    try {
      const { data } = await getBookedJobs(date);
      setDateData({ date, dateTimeArray: data });
      setDateLoading(false);
    } catch (error) {
      console.log(error);
      setDateLoading(false);
    }
  };

  useEffect(() => {
    const stepTwoData = retrieveData("stepTwoData");
    if (stepTwoData?.hearAboutUs) {
      const data = { ...stepTwoData };
      getBookedJobsData(data.date);
      reset({ ...data });
    }
    scrollToTop();
  }, []);

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      if (moment(date).day() === 0 || moment(date).day() === 6) {
        // Add class for weekends
        return "weekend";
      }
      if (moment(date).isSame(moment(), "day")) {
        // Add class for the current day
        return "current-day";
      }
    }
    return null; // No custom class
  };
  const isMobile = useMediaQuery("(max-width:800px)");
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={500} color={"primary"}>
            When are you looking to move?<sup>*</sup>
          </Typography>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            flexWrap={isMobile ? "wrap" : "nowrap"}
            alignItems={"flex-start"}
            gap={!isMobile ? 10 : 5}
          >
            <div style={{ width: isMobile ? "100%" : "50%" }}>
              <Typography sx={{ mt: 3 }} color={"primary"}>
                Choose a date
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Controller
                  name={"date"}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Calendar
                      tileClassName={tileClassName}
                      onChange={(newValue) => {
                        onChange(newValue);
                        getBookedJobsData(newValue);
                        reset({ ...getValues(), time: "" });
                      }}
                      value={value}
                      minDate={new Date()}
                    />
                  )}
                />
                {errors.date && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {errors.date.message}
                  </FormHelperText>
                )}
              </FormControl>
            </div>
            <div
              style={{
                width: isMobile ? "100%" : "40%",
              }}
            >
              <Typography
                sx={{ mt: !isMobile ? 3 : 0, mb: 1 }}
                color={"primary"}
              >
                Choose a time
              </Typography>
              <div>
                <FormControl fullWidth>
                  <Controller
                    name={"time"}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <TimeChips
                        dateLoading={dateLoading}
                        dateData={dateData}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </FormControl>
                {errors.time && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {errors.time.message}
                  </FormHelperText>
                )}
              </div>
            </div>
          </Stack>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={500} color={"primary"}>
            Any other relevant information?
          </Typography>
          <TextInputField
            controller={{ control: control, errors: errors.otherInformation }}
            name={"otherInformation"}
            minRows={3}
          />
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={500} color={"primary"}>
            Where did you learn about us?<sup>*</sup>
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <Controller
              name="hearAboutUs"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <Select size="small" value={value} onChange={onChange}>
                  {platform.map((platform, idx) => (
                    <MenuItem key={idx} value={platform}>
                      {platform}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.hearAboutUs && (
              <FormHelperText sx={{ color: "error.main" }}>
                {errors.hearAboutUs.message}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
        <Stack
          mt={3}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Button size="small" variant="outlined" onClick={onStepDecrement}>
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            type="submit"
            disabled={!isValid}
          >
            Next
          </Button>
        </Stack>
        <Note />
      </form>
    </div>
  );
}
